import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BLOG_LIST } from "@/data/blogArticles";
import { Clock, ArrowRight, BookOpen } from "lucide-react";

function useSEO() {
    useEffect(() => {
        document.title = "Blog Renforteo — Guides pratiques pour pros et entreprises";
        const setMeta = (name, content, attr = "name") => {
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        const desc =
            "Guides pratiques Renforteo : devenir livreur-monteur en auto-entrepreneur, combien gagne un plongeur traiteur, devenir serveur à Lyon, renfort service événementiel.";
        setMeta("description", desc);
        setMeta("og:title", "Blog Renforteo — Guides pratiques");
        setMeta("og:description", desc);
        setMeta("og:type", "website");
        setMeta("og:url", "https://renforteo.fr/blog");

        let canon = document.querySelector("link[rel=canonical]");
        if (!canon) {
            canon = document.createElement("link");
            canon.setAttribute("rel", "canonical");
            document.head.appendChild(canon);
        }
        canon.setAttribute("href", "https://renforteo.fr/blog");
    }, []);
}

export default function BlogPage() {
    useSEO();

    const sorted = [...BLOG_LIST].sort((a, b) => b.published_at.localeCompare(a.published_at));

    return (
        <div data-testid="blog-page">
            <Header />

            <section className="bg-[#0B192C] text-white" data-testid="blog-hero">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20">
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#F5A623]/40 bg-[#F5A623]/10 px-4 py-1.5 mb-6">
                        <BookOpen size={14} className="text-[#F5A623]" />
                        <span className="text-xs uppercase tracking-widest font-semibold text-[#F5A623]">
                            Guides pratiques
                        </span>
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight mb-5">
                        Le blog Renforteo.
                    </h1>
                    <p className="text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">
                        Guides pas à pas, tarifs 2026 et démarches réglementaires — pour les
                        professionnels indépendants et les entreprises qui recrutent.
                    </p>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20">
                <div className="grid md:grid-cols-2 gap-6">
                    {sorted.map((a) => (
                        <Link
                            key={a.slug}
                            to={`/blog/${a.slug}`}
                            className="group sd-card overflow-hidden border-2 border-transparent hover:border-[#F5A623] transition-all"
                            data-testid={`blog-card-${a.slug}`}
                        >
                            <div className="aspect-[16/9] overflow-hidden bg-slate-100 relative">
                                <img
                                    src={a.hero_img}
                                    alt={a.h1}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute top-3 left-3">
                                    <span className="inline-block bg-[#0B192C]/85 backdrop-blur text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full">
                                        {a.category}
                                    </span>
                                </div>
                            </div>
                            <div className="p-5 md:p-6">
                                <h2 className="font-display text-lg md:text-xl leading-tight text-[#0B192C] mb-3 group-hover:text-[#F5A623] transition-colors">
                                    {a.h1}
                                </h2>
                                <p className="text-sm text-slate-500 leading-relaxed mb-4 line-clamp-3">
                                    {a.excerpt}
                                </p>
                                <div className="flex items-center justify-between gap-3 text-xs text-slate-400">
                                    <span className="inline-flex items-center gap-1.5">
                                        <Clock size={12} />
                                        {a.read_time_min} min de lecture
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-[#0B192C] font-semibold group-hover:text-[#F5A623] transition-colors">
                                        Lire
                                        <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            <Footer />
        </div>
    );
}
